<template>
    <div class="resources">
        <div class="resources-header">
            <div class="title">
                <span class="icon">🔧</span>
                <h2>Machines & Tools</h2>
            </div>

            <div class="s" v-if="isFactorySupervisor || isFactoryAdmin"><button class="btn pointer" @click="openAddResourceModal">+ Add Resource</button></div>
        </div>

        <div class="fw center loader" v-if="loading">
          <loading-circle />
        </div>
        <div class="cards" v-else>
            <div v-for="resource in resources" :key="resource.id" class="card"
                :class="resource.resource_state_custom_list_value?.name === 'MAINTENANCE' ? 'maintenance' : 'active'">
                <div class="card-header">
                    <h3 class="card-name" v-overflow-tooltip>{{ resource.name }}</h3>
                    <span
                        @click="UpdateFacResourceStatus(resource)"
                        class="power"
                        :class="resource.resource_state_custom_list_value?.name === 'MAINTENANCE' ? 'is-maintenance' : 'is-active'"
                    >
                        ⏻
                    </span>
                </div>
                <div class="card-subtext" v-overflow-tooltip>
                   <span class="s text-grey">{{ resource?.sequence_value }}</span>
                </div>
                <div class="tags">
                    <span :class="['tag', resource.resource_state_custom_list_value?.name === 'MAINTENANCE' ? 'maintenance' : 'active']">{{ resource.resource_state_custom_list_value?.name === 'MAINTENANCE' ? 'MAINTENANCE' : 'ACTIVE' }}</span><span class="tag neutral">{{ resource?.core_material_master?.resource_type_value?.name  }}</span>
                </div>
            </div>
        </div>
        <modal :open="addResourceModalObject.open" @close="addResourceModalObject.open = false" title="Add Resource" description="Add a new machine or tool to the factory resources">
            <add-resource v-if="addResourceModalObject.open" @cancel="addResourceModalObject.open = false" @resource-added="handleResourceCreation"/>
        </modal>
    </div>
</template>

<script>
import Modal from '@/components/common/modal.vue'
import AddResource from './addResource.vue'
import { GetAllResourceInstances, UpdateFactoryResources } from '@/api/apis/factory'
import { alert, success } from '@/plugins/notification'
import LoadingCircle from '@/components/common/loadingCircle.vue'
import { mapGetters } from 'vuex'
import { ResourceState, GetTenantConfigTypes } from '@/api'
import { startGlobalCursorLoader, stopGlobalCursorLoader } from '@/utils/globalCursorLoader'
export default {
  name: 'resources',
  components: {
    Modal,
    AddResource,
    LoadingCircle
  },
  data () {
    return {
      addResourceModalObject: {
        open: false
      },
      resources: [],
      loading: false
    }
  },
  computed: {
    ...mapGetters(['isFactorySupervisor', 'isFactoryAdmin'])
  },
  mounted () {
    this.init()
  },
  methods: {
    async openAddResourceModal () {
      startGlobalCursorLoader()
      try {
        const res = await GetTenantConfigTypes()
        const sequenceTemplateDefault = res?.tenant_defaults?.[0]?.tenant_feature_configuration?.FACTORIES?.RESOURCES?.SEQUENCE_TEMPLATE_DEFAULT

        if (!sequenceTemplateDefault) {
          alert('No sequence Id is associated with your tenant')
          return
        }

        this.addResourceModalObject.open = true
      } catch (err) {
        alert('Failed to validate tenant configuration')
      } finally {
        stopGlobalCursorLoader()
      }
    },
    init () {
      this.loading = true
      GetAllResourceInstances()
        .then(response => {
          this.resources = response.factory_resource_association
        })
        .catch(() => {
          alert('Failed to fetch resources')
        }).finally(() => {
          this.loading = false
        })
    },
    async UpdateFacResourceStatus (resource) {
      try {
        const res = await ResourceState()
        const listValues = res?.custom_list_values || []
        const currentState = resource.resource_state_custom_list_value?.name
        const targetStateName = currentState === 'MAINTENANCE' ? 'RUNNING' : 'MAINTENANCE'
        const targetStateObj = listValues.find(item => item.name === targetStateName)
        const updateObj = {
          resource_state: targetStateObj.id
        }
        await UpdateFactoryResources(resource.id, updateObj)
        success(`Resource set to ${targetStateName === 'RUNNING' ? 'ACTIVE' : 'MAINTENANCE'}`)
        this.init()
      } catch (err) {
        console.error('Update failed:', err)
        alert('Failed to update resource status')
      }
    },
    handleResourceCreation () {
      this.addResourceModalObject.open = false
      this.init()
    }
  }
}
</script>

<style scoped lang="scss">
.resources {
    &-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 16px;

        .title {
            display: flex;
            align-items: center;
            gap: 6px;

            h2 {
                font-size: var(--text-base);
                font-weight: 400;
            }
        }
    }
}

.loader {
  height: calc(100vh - 312px);
}

.cards {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 14px;
    overflow-y: auto;
    max-height: calc(100vh - 312px);

    .card {
        background: #ffffff;
        border-radius: 8px;
        padding: 12px 14px;
        border: 1px solid rgba(0, 0, 0, 0.08);
        border-left: 3px solid transparent;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
        transition: box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease;

        &:hover {
            box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
        }

        &.active {
            border-left-color: #22c55e;
        }

        &.maintenance {
            border-left-color: #ef4444;
        }

        &-name {
            text-overflow: ellipsis;
            overflow: hidden;
            white-space: nowrap;
            max-width: 160px;
        }

        &-subtext {
            text-overflow: ellipsis;
            overflow: hidden;
            white-space: nowrap;
            max-width: 160px;
        }

        &-header {
            display: flex;
            justify-content: space-between;
            align-items: center;

            h3 {
                font-size: var(--text-base);
                font-weight: 400;
            }

            .power {
                font-size: var(--text-xl);
                opacity: 0.7;
                cursor: pointer;
                transition: opacity 0.2s, color 0.2s;
                &.is-active {
                    color: #22c55e; // Green
                }
                &.is-maintenance {
                    color: #ef4444; // Red
                }
                &:hover {
                    opacity: 1;
                }
            }
        }

        .tags {
            display: flex;
            gap: 6px;
            margin: 8px 0 12px;

            .tag {
                font-size: var(--text-xs2);
                padding: 3px 6px;
                border-radius: 5px;
                font-weight: 600;

                &.active {
                    background: #dcfce7;
                    color: #166534;
                }

                &.maintenance {
                    background: #fee2e2;
                    color: #991b1b;
                }

                &.neutral {
                    background: #f1f5f9;
                    color: #334155;
                }
            }
        }
    }
}
</style>
